import {
  getParentBPId,
  getPartnerDisplay,
  getServicesFromPermissionsByKey,
  joinNameAndDescription,
} from '@sr/utils';
import {
  Country,
  type PartnerTreeNodeV2,
  type StenaPermissions,
  type UserBusinessPartner,
} from '@sr/types';

import { usesDatalakeFieldShape } from '../utils/partnerRecordOrigin';
import { getNameAndTextForFnoPartner } from './fnoNameUtil';
import { getFullLabelForFire, getNameAndTextForFirePartner } from './fireNameUtil';
import { type LocationListItemsType } from './types';

export const LocationCountTemplate = '#COUNT';

const getBadgeFromPermissions = (
  dwKey: string,
  permissions?: UserBusinessPartner[],
): string | undefined => {
  if (!permissions) return undefined;

  const services = getServicesFromPermissionsByKey(dwKey, permissions);

  if (!services) return undefined;

  for (const service of services) {
    if (service.csrdEnterprise) return 'csrdEnterprise';
    if (service.csrdPremium) return 'csrdPremium';
  }

  return undefined;
};

type GetDropdownItemsProps = {
  partners: PartnerTreeNodeV2[];
  shouldExcludeInactiveValues: boolean;
  level: number;
  parentName?: string;
  allowSelectingBusinessPartners?: boolean;
  includeBusinessAddress?: boolean;
  parentValue?: string;
  permissions?: StenaPermissions['businessPartners'];
};

export const partnerTreesToListDropdown = ({
  partners,
  shouldExcludeInactiveValues,
  level,
  parentName,
  allowSelectingBusinessPartners,
  includeBusinessAddress,
  parentValue,
  permissions,
}: GetDropdownItemsProps): LocationListItemsType[] => {
  const items = partners
    .toSorted((a, b) => sortPartners(a, b, level, parentName))
    .map((partner) => {
      const mergedChild = getMergedChild(partner);
      const _isMergedItem = !!mergedChild;
      const _isSamlingsMotpart = isSamlingsMotpart(partner);

      const { nameAndText, addressAndId } = makePartnerLabels(partner, level, parentName);

      const isActive = getIsPartnerActive(partner);
      const partnerIsUnselectable = getIsPartnerUnselectable(
        partner,
        !!allowSelectingBusinessPartners,
        !!includeBusinessAddress,
      );

      const shouldExcludeDueToInactive =
        !isActive && shouldExcludeInactiveValues && !partner.item.hasActiveChildren;

      const isUnselectable = partnerIsUnselectable || shouldExcludeDueToInactive;

      const dwKey = mergedChild ? mergedChild.item.dwKey : partner.item.dwKey;

      const childCount = partner.item.childCount;
      const locationCount = partner.item.locationCount;

      const isLocation = getIsLocation(partner, _isMergedItem);

      let type: 'group' | 'value' = isUnselectable ? 'group' : 'value';

      if (_isMergedItem) {
        type = 'value';
      }

      const fullLabel = getFullLabel(partner);

      let children = partner.children.filter((child) => child !== mergedChild);

      if (_isSamlingsMotpart && !mergedChild) {
        children = children.filter(
          (child) => child.item.type === 'BUSINESSPARTNER' || child.item.type === 'BusinessPartner',
        );
      }

      const showChildren = !mergedChild || children.length > 0;

      const item = {
        value: dwKey,
        label: nameAndText,
        altLabel: fullLabel,
        parentValue,
        meta: {
          description: addressAndId,
          childCount: childCount ?? 0,
          locationCount: locationCount,
          isLocation: !!isLocation,
          isActive,
          shouldExcludeInactiveValues,
          businessAddressId: partner.item.businessAddressId,
          isSamlingsMotpart: _isSamlingsMotpart,
          isUnselectable,
          activeChildren: !!partner.item.hasActiveChildren,
          source: partner.item.source,
          isMerged: _isMergedItem,
          badge: getBadgeFromPermissions(dwKey, permissions),
        },
        type,
        children: showChildren
          ? partnerTreesToListDropdown({
              partners: children,
              shouldExcludeInactiveValues,
              level: level + 1,
              parentName: partner.item.name,
              includeBusinessAddress: includeBusinessAddress,
              allowSelectingBusinessPartners,
              parentValue: dwKey,
              permissions,
            })
          : [],
      } satisfies LocationListItemsType;

      return item;
    });

  return items;
};

const isSamlingsMotpart = (partner: PartnerTreeNodeV2) => {
  if (partner.item.source === 'FIRE') {
    return partner.children.length > 0;
  }
  const isMergeItem = isMergedItem(partner);
  const bpChildren = partner.children.filter((child) => child.item.type === 'BUSINESSPARTNER');

  return isMergeItem && bpChildren.length > 0;
};

const getIsLocation = (partner: PartnerTreeNodeV2, isMerged: boolean) => {
  if (isMerged) {
    return true;
  }

  if (partner.item.source === 'CS') {
    return partner.item.isLocation && !partner.children.length;
  }

  return partner.item.isLocation;
};

/**
 * The delivery address a business partner row stands in for: its only delivery address, or
 * for Finnish partners the main address, which the partner's own orders are booked on.
 */
export const getMergedChild = (partner: PartnerTreeNodeV2): PartnerTreeNodeV2 | undefined => {
  if (!shouldUseNewPickerForSource(partner.item.source)) return undefined;
  if (partner.item.type === 'DELIVERYADRESS') return undefined;

  const deliveryAddresses = partner.children.filter(
    (child) => child.item.type === 'DELIVERYADRESS',
  );

  if (deliveryAddresses.length === 1) return deliveryAddresses[0];

  if (partner.item.companyId !== Country.FINLAND || !partner.item.businessAddressId) {
    return undefined;
  }

  return deliveryAddresses.find((child) => child.item.dwKey === partner.item.businessAddressId);
};

export const isMergedItem = (partner: PartnerTreeNodeV2) => !!getMergedChild(partner);

const getFullLabel = (partner: PartnerTreeNodeV2) => {
  if (partner.item.source === 'FIRE') {
    return getFullLabelForFire(partner);
  }

  if (!shouldUseNewPickerForSource(partner.item.source)) {
    return;
  }

  const mergedChild = getMergedChild(partner);

  if (mergedChild) {
    const label = joinNameAndDescription(partner.item.name, mergedChild.item.description);

    return `${label} (${partner.item.id})`;
  }

  const { nameAndText } = getPartnerDisplay(partner.item);
  const parentId = getParentBPId(partner.item.dwKey);

  return `${nameAndText} (${parentId})`;
};

const getIsPartnerUnselectable = (
  partner: PartnerTreeNodeV2,
  allowSelectingBusinessPartners: boolean,
  includeBusinessAddress: boolean,
) => {
  if (allowSelectingBusinessPartners) return false;
  if (shouldUseNewPickerForSource(partner.item.source)) {
    const _isLoc = getIsLocation(partner, isMergedItem(partner));

    if (!includeBusinessAddress) {
      return !_isLoc;
    }

    const hasBusinessAddressId = !!partner.item.businessAddressId;

    return !_isLoc && !hasBusinessAddressId;
  }

  return false;
};

const makePartnerLabels = (partner: PartnerTreeNodeV2, level: number, parentName?: string) => {
  if (shouldUseNewPickerForSource(partner.item.source)) {
    return getNameAndTextForFnoPartner(partner, level, parentName);
  } else if (partner.item.source === 'FIRE') {
    return getNameAndTextForFirePartner(partner, level, parentName);
  }

  return getPartnerDisplay(partner.item);
};

export const shouldUseNewPickerForSource = usesDatalakeFieldShape;

export const getIsPartnerActive = (partner: PartnerTreeNodeV2) => {
  const mergedChild = getMergedChild(partner);

  if (mergedChild) {
    return mergedChild.item.isActive;
  }

  if (partner.item.companyId === Country.SWEDEN) {
    return partner.item.isActive;
  }

  return partner.item.isActivePickupPlace;
};

export const hasInactiveChildren = (partners: PartnerTreeNodeV2[]): boolean => {
  return partners.some((partner) => {
    const isActive = getIsPartnerActive(partner);

    if (!isActive) {
      return true;
    }

    return hasInactiveChildren(partner.children);
  });
};

const sortPartners = (
  a: PartnerTreeNodeV2,
  b: PartnerTreeNodeV2,
  level: number,
  parentName?: string,
) => {
  const { nameAndText: nameAndTextA } = makePartnerLabels(a, level, parentName);
  const { nameAndText: nameAndTextB } = makePartnerLabels(b, level, parentName);

  if (a.item.name.startsWith('[Confidential]') && b.item.name.startsWith('[Confidential]')) {
    return nameAndTextA.localeCompare(nameAndTextB);
  }

  if (a.item.name.startsWith('[Confidential]')) {
    return 1;
  }
  if (b.item.name.startsWith('[Confidential]')) {
    return -1;
  }

  if (!shouldUseNewPickerForSource(a.item.source) || !shouldUseNewPickerForSource(b.item.source)) {
    const textA = getPartnerDisplay(a.item).nameAndText;
    const textB = getPartnerDisplay(b.item).nameAndText;

    return textA.localeCompare(textB, 'sv');
  }

  return nameAndTextA.localeCompare(nameAndTextB, 'sv');
};
